import type { PageInfo } from '@analytics/models';
import { DOCUMENT } from '@angular/common';
import { inject, Injectable, Injector } from '@angular/core';
import { ActivatedRoute, NavigationEnd, Router } from '@angular/router';
import { ENVIRONMENT_CONFIG } from '@core/config-angular';
import { ConsentService } from '@core/consent';
import {
  JssStateActionTypes,
  JssStateMetaTitleUpdated,
  JssStateRootPlaceholderLoaded,
} from '@core/jss-routing';
import { NotFound, NotFoundRouteName, ServerError } from '@core/models';
import { PlatformService } from '@core/platform';
import { WINDOW } from '@innogy/utils-dom';
import { Actions, ofType } from '@ngrx/effects';
import {
  type Observable,
  filter,
  map,
  mergeMap,
  pairwise,
  share,
  startWith,
  switchMap,
  take,
  withLatestFrom,
  zip,
} from 'rxjs';

import { AnalyticsUtils } from '../analytics-utils.service';

@Injectable({
  providedIn: 'root',
})
export class PageMetadataService {
  // Routing
  readonly #router = inject(Router);
  readonly #activatedRoute = inject(ActivatedRoute);
  // Store management
  readonly #actions$ = inject(Actions);
  // Utils
  readonly #injector = inject(Injector);
  readonly #analyticsUtils = inject(AnalyticsUtils);
  readonly #consentService = inject(ConsentService);
  readonly #platformService = inject(PlatformService);
  readonly #windowRef = inject(WINDOW);
  readonly #document = inject(DOCUMENT);

  /**
   * Observable that pipes all navigation events that are not on a 'not found' or 'error' page.
   */
  readonly #nonErrorNavigationEnd$: Observable<NavigationEnd> =
    this.#router.events.pipe(
      filter((event): event is NavigationEnd => event instanceof NavigationEnd),
      filter((event) => !event.url.startsWith(`/${NotFoundRouteName}`)),
      filter((event) => !event.url.startsWith(`/${NotFound}`)),
      filter((event) => !event.url.startsWith(`/${ServerError}`))
    );

  /**
   * Observable containing pairs of navigation events that are not on a 'not found' or 'error' page.
   * Returns data in a [previous route, current route] fashion.
   */
  readonly #nonErrorNavigationEndPairs$ = this.#nonErrorNavigationEnd$.pipe(
    startWith(null),
    pairwise()
  );

  /**
   * Observable piping changes to the page meta title.
   */
  readonly #titleUpdated$ = this.#actions$.pipe(
    ofType<JssStateMetaTitleUpdated>(JssStateActionTypes.META_TITLE_UPDATED),
    map((action) => action.payload)
  );

  /**
   * Observable indicating whether a 'root placeholder' has been loaded after a navigation event.
   * This means that the JSS state was updated and the new page-data is available.
   */
  readonly #rootPlaceholderLoaded$ = this.#nonErrorNavigationEnd$.pipe(
    switchMap(() =>
      this.#actions$.pipe(
        ofType<JssStateRootPlaceholderLoaded>(
          JssStateActionTypes.ROOT_PLACEHOLDER_LOADED
        ),
        take(1)
      )
    )
  );

  /**
   * Determines a referrer for the currently active route.
   * - If we're on the first active route on the client, we get the `referrer` from the document.
   * - Otherwise if there's a last active route, we construct a URL based on the last active route.
   * - Otherwise we return an empty string.
   * @param lastActiveRoute the last route that has been active.
   * @returns URL containing the page that has referred us to the currently active route.
   */
  #getReferrer(lastActiveRoute: NavigationEnd | null): string {
    /*istanbul ignore next*/
    // Navigated from external site, document.referrer is set
    if (lastActiveRoute == null && this.#platformService.isClient()) {
      return this.#document.referrer;
    } else if (this.#document.location != null && lastActiveRoute) {
      const location = this.#document.location;
      // Internal navigation, use lastRoute as referrer
      return `${location.protocol}//${location.host}${lastActiveRoute.url}`;
    }
    return '';
  }

  #getEnvironment() {
    return this.#injector.get(ENVIRONMENT_CONFIG).environment;
  }

  /**
   * Formats a JavaScript Date object into the format 'yyyy-MM-dd HH:mm:ss'.
   *
   * @param {Date} date - The Date object to be formatted.
   * @returns {string} The formatted date string in 'yyyy-MM-dd HH:mm:ss' format.
   */
  #formatTimestamp(date: Date) {
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0'); // Month is 0-based
    const day = String(date.getDate()).padStart(2, '0');
    const hours = String(date.getHours()).padStart(2, '0');
    const minutes = String(date.getMinutes()).padStart(2, '0');
    const seconds = String(date.getSeconds()).padStart(2, '0');

    return `${year}-${month}-${day} ${hours}:${minutes}:${seconds}`;
  }

  /**
   * Shared observable stream containing a 'PageInfo' object for the currently active page.
   *
   * The observable contains:
   * - page url the event is triggered from, e.g. `https://www.essent.nl/energie`.
   * - page path the event is triggered from, e.g. `/energie`.
   * - and title the event is triggered from, e.g. `Energie van vandaag: stroom en gas | Essent`.
   * - referrer the current page is coming from, e.g. `https://www.google.com/q=goedkope-stroom`.
   * - domain the event is triggered from, e.g. `www.essent.nl`.
   * - user Agent data of device the event is triggered from.
   */
  public pageInfo$ = zip(
    this.#nonErrorNavigationEndPairs$,
    this.#titleUpdated$,
    this.#rootPlaceholderLoaded$
  ).pipe(
    withLatestFrom(this.#activatedRoute.queryParams),
    mergeMap(async ([[[lastRoute], title], queryParams]): Promise<PageInfo> => {
      return Promise.resolve({
        url: this.#analyticsUtils.getUrlWithoutParamsAndFragments(),
        category: await this.#analyticsUtils.getSiteContext(),
        path: this.#analyticsUtils.getPathWithoutParamsAndFragments(),
        name: this.#analyticsUtils.getPageName(),
        consent: this.#consentService.getCookieConsentLevel(),
        domain: new URL(this.#document.URL).hostname,
        referrer: this.#getReferrer(lastRoute),
        title,
        userAgent: this.#windowRef?.navigator?.userAgent ?? '',
        params: queryParams,
        timestamp: this.#formatTimestamp(new Date()),
        environment: this.#getEnvironment(),
      });
    }),
    share()
  );
}
